import React, { useEffect } from "react";
import { IonContent, IonPage, IonSpinner, IonText } from "@ionic/react";

import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import "./WeHaveFinished.css";
import PremiumWelcomeModal from "../../../components/modals/PremiumWelcomeModal/PremiumWelcomeModal";
import usePlan from "../../../hooks/usePlan";
import useProtocolsPageHeader from "../../../hooks/useProtocolsPageHeader";
import { JOURNEYS_INTERNAL_PATH } from "../../../utils/communityDeepLinks";
import {
  liteWelcomeCallout,
  markLiteWelcomeShown,
} from "../../../utils/premiumLiteJourneyIntro";

/**
 * /onboarding-complete — where the last onboarding step lands.
 *
 * There used to be a Guest / Premium / Free chooser here. The client removed it
 * (17/09): a member who just finished onboarding goes straight on, and only a
 * tap on "ISCRIVITI GRATIS" enrols anyone.
 *  - Premium: the welcome popup, then the app.
 *  - Already FREE: their program.
 *  - Everyone else: the Home, free to look around. Until 05/11/2026 a member
 *    eligible for FREE was taken to the Free Programme screen first; the client
 *    dropped that step. Someone who came through the free-week link
 *    (/app?sezione=gratis) still gets that page: CommunityDeepLinkHandler
 *    replays the link once they reach the Home.
 * Onboarding is over when RouteAuth sees the refreshed profile (it redirects
 * here with `fromOnboarding`), or when the last step navigates here first.
 */
const WeHaveFinished = () => {
  const history = useHistory();
  const location = useLocation();

  const user = useSelector((s) => s.user?.user);
  const subscriber = useSelector((s) => s.user?.master_club_subscriber);

  const subscriptionName = (
    subscriber?.subscription?.subscription_type?.name ??
    user?.subscription_label?.name ??
    "guest"
  )
    .toLowerCase()
    .trim();

  // Set by RouteAuth's redirect when onboarding completes (and by
  // TypeOfDietPage when it gets there first).
  const fromOnboarding = location.state?.fromOnboarding === true;

  const isActivePaidPremium =
    subscriptionName === "premium" &&
    subscriber?.subscription?.is_enabled === true;
  const showPremiumWelcome = isActivePaidPremium && fromOnboarding;
  const greeting = subscriber?.sex === "female" ? "Benvenuta" : "Benvenuto";
  const { isPremiumLite } = usePlan();
  // Premium Lite: the first step is the journey (client, 23/10/2026).
  const liteWelcome = showPremiumWelcome && isPremiumLite;
  const { premiumLite } = useProtocolsPageHeader(liteWelcome);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (liteWelcome && userId) markLiteWelcomeShown(userId);
  }, [liteWelcome, userId]);

  useEffect(() => {
    // Only an arrival on this route: the entries this step navigates to must not
    // route again.
    if (location.pathname !== "/onboarding-complete") return undefined;
    // The popup leads on from there.
    if (showPremiumWelcome) return undefined;

    let cancelled = false;
    const go = (path, state) => {
      if (!cancelled) history.replace(path, state);
    };

    const route = () => {
      if (isActivePaidPremium) return go("/home");
      if (subscriptionName === "free") return go("/corso");
      // Enrolled in a program that hasn't reached its preparation week: the
      // strip above the tab bar carries the date.
      if (subscriber?.upcoming_free_program) return go("/home");

      return go("/home", fromOnboarding ? { firstLogin: true } : undefined);
    };

    route();
    return () => {
      cancelled = true;
    };
    // Once per arrival (history entry), not once per mount. The router outlet is
    // a single Suspense boundary: while the next page's code loads, React keeps
    // this page alive and shows it again for a new arrival. A mount-only effect
    // then never routed that arrival — a new member sat on the spinner after
    // onboarding (17/09).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  if (showPremiumWelcome) {
    return (
      <IonPage>
        <IonContent fullscreen className="wf-premium-welcome-content">
          <PremiumWelcomeModal
            isOpen
            title={`🎉 ${greeting} nella casa di Dieta Social!`}
            subtitle={`Il tuo account ${isPremiumLite ? "Premium Lite" : "Premium"} è già attivo. Ti auguriamo un buon percorso!`}
            lite={isPremiumLite}
            liteCallout={isPremiumLite ? liteWelcomeCallout(premiumLite) : null}
            primaryLabel={isPremiumLite ? "Scegli il tuo percorso" : "Inizia"}
            onPrimary={() => history.replace(isPremiumLite ? JOURNEYS_INTERNAL_PATH : "/home")}
            onDismiss={() => history.replace("/home")}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="ds-ready-page ds-ready-page--loading">
      <IonContent fullscreen>
        <div className="ds-ready-loading">
          <IonSpinner name="crescent" color="primary" />
          <IonText>Preparazione percorso…</IonText>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default WeHaveFinished;
